// apps/web/src/lib/painel/home/brilho.ts — Fase 17.8, Plano 12 (D-14; UI-SPEC U-23 e "real-time" da UI-SPEC).
// Decisões do brilho de ~1 s por card, em funções puras: quais cards mudaram entre duas respostas da home, se o card pode
// brilhar agora (no máximo 1 brilho por card a cada 5 s, só para mudança vinda do stream) e os atributos de marcação.
// Ids de card: 'heroi', 'apoio-1'..'apoio-3', 'etapa-{id}' e 'item-{id}'.
import { CATALOGO_METRICAS } from './catalogo-metricas';
import type { OrigemNumero } from '../../../components/painel/home/numero-ao-vivo';

/** Calma por card: depois de brilhar, o card só brilha de novo passados 5 s. */
export const CALMA_BRILHO_MS = 5000;
/** Duração do brilho (e do contorno estático com movimento reduzido). */
export const DURACAO_BRILHO_MS = 1000;

export type OrigemBrilho = OrigemNumero;

type Registro = Readonly<Record<string, unknown>>;

const comoRegistro = (valor: unknown): Registro | null => (typeof valor === 'object' && valor !== null && !Array.isArray(valor) ? (valor as Registro) : null);

const comoLista = (valor: unknown): readonly unknown[] => (Array.isArray(valor) ? (valor as readonly unknown[]) : []);

/** Valor numérico de uma métrica medida; indisponível, sem medição ou ilegível = `undefined`. */
function valorMedido(metrica: unknown): number | undefined {
  const m = comoRegistro(metrica);
  if (m === null || m.estado !== 'MEDIDO') return undefined;
  const numero = typeof m.valor === 'number' ? m.valor : Number(m.valor);
  return Number.isFinite(numero) ? numero : undefined;
}

/** Mudou de um número para outro (aparecer onde não havia nada não é "mudança ao vivo"). */
const mudou = (antes: number | undefined, depois: number | undefined): boolean => antes !== undefined && depois !== undefined && antes !== depois;

function totalDaEtapa(etapa: Registro): number | undefined {
  const contagem = comoRegistro(etapa.contagem);
  return contagem !== null && contagem.estado === 'MEDIDO' && typeof contagem.total === 'number' ? contagem.total : undefined;
}

/**
 * Cards cujo valor mudou entre duas respostas da home: 'heroi' (valor do herói), 'apoio-N' (valor do kv de chip N), 'etapa-{id}'
 * (contagem da etapa) e 'item-{id}' (item novo da lista "Precisa de você"). Só `geradoEm` mudando não é mudança; item removido
 * não brilha (sai com fade). Entradas fora do formato esperado são ignoradas.
 */
export function mudancasEntre(anterior: unknown, atual: unknown): readonly string[] {
  const antes = comoRegistro(anterior);
  const depois = comoRegistro(atual);
  if (antes === null || depois === null) return [];
  const cards: string[] = [];

  if (mudou(valorMedido(antes.heroi), valorMedido(depois.heroi))) cards.push('heroi');

  const apoioAntes = comoLista(antes.apoio).map(comoRegistro);
  comoLista(depois.apoio).forEach((bruto, posicao) => {
    const item = comoRegistro(bruto);
    if (item === null || typeof item.id !== 'string') return;
    const correspondente = apoioAntes.find((candidato) => candidato?.id === item.id);
    if (correspondente === undefined || correspondente === null) return;
    if (mudou(valorMedido(correspondente.metrica), valorMedido(item.metrica))) {
      cards.push(`apoio-${CATALOGO_METRICAS[item.id]?.chip ?? posicao + 1}`);
    }
  });

  const esteiraAntes = comoRegistro(antes.esteira);
  const esteiraDepois = comoRegistro(depois.esteira);
  if (esteiraAntes?.estado === 'MEDIDO' && esteiraDepois?.estado === 'MEDIDO') {
    const etapasAntes = comoLista(esteiraAntes.etapas).map(comoRegistro);
    for (const bruta of comoLista(esteiraDepois.etapas)) {
      const etapa = comoRegistro(bruta);
      if (etapa === null || typeof etapa.id !== 'string') continue;
      const correspondente = etapasAntes.find((candidata) => candidata?.id === etapa.id);
      if (correspondente === undefined || correspondente === null) continue;
      if (mudou(totalDaEtapa(correspondente), totalDaEtapa(etapa))) cards.push(`etapa-${etapa.id}`);
    }
  }

  const listaAntes = comoRegistro(antes.precisaDeVoce);
  const listaDepois = comoRegistro(depois.precisaDeVoce);
  if (listaAntes?.estado === 'MEDIDO' && listaDepois?.estado === 'MEDIDO') {
    const idsAntes = new Set(idsDosItens(listaAntes.itens));
    for (const id of idsDosItens(listaDepois.itens)) {
      if (!idsAntes.has(id)) cards.push(`item-${id}`);
    }
  }

  return cards;
}

function idsDosItens(itens: unknown): string[] {
  const ids: string[] = [];
  for (const bruto of comoLista(itens)) {
    const item = comoRegistro(bruto);
    if (item !== null && typeof item.id === 'string') ids.push(item.id);
  }
  return ids;
}

/**
 * O card pode brilhar agora? Só mudança vinda do stream ('ao-vivo') brilha; primeira carga, retorno da aba e recarga de 2 min
 * são silenciosos. Depois de brilhar, o card espera 5 s (calma por card).
 */
export function deveBrilhar(card: string, origem: OrigemBrilho, ultimoBrilhoMs: number | undefined, agoraMs: number): boolean {
  if (card === '' || origem !== 'ao-vivo') return false;
  if (ultimoBrilhoMs === undefined) return true;
  return agoraMs - ultimoBrilhoMs >= CALMA_BRILHO_MS;
}

/** Itens novos e removidos entre duas listas de ids (a ordem das listas não importa). */
export function diferencaDeIds(anteriores: readonly string[], atuais: readonly string[]): { readonly novos: readonly string[]; readonly removidos: readonly string[] } {
  const antes = new Set(anteriores);
  const depois = new Set(atuais);
  return { novos: atuais.filter((id) => !antes.has(id)), removidos: anteriores.filter((id) => !depois.has(id)) };
}

export interface AtributosBrilho {
  readonly 'data-brilho'?: 'true';
  readonly 'data-brilho-estatico'?: 'true';
}

/**
 * Atributos do card que mudou: `data-brilho` (brilho animado de ~1 s) ou, com movimento reduzido, `data-brilho-estatico`
 * (contorno de 2px por 1 s, sem animação). Card fora do conjunto não recebe nenhum.
 */
export function atributosDoBrilho(card: string, brilhos: readonly string[] | undefined, reduzido: boolean): AtributosBrilho {
  if (brilhos === undefined || !brilhos.includes(card)) return {};
  return reduzido ? { 'data-brilho-estatico': 'true' } : { 'data-brilho': 'true' };
}
